import React, { useMemo, useState } from 'react';
import EditorDialog, { FieldDef } from './EditorDialog';
import {
  upsertJobType,
  upsertAppliance,
  upsertComponent,
  upsertAttributeOption,
  deleteJobType,
  deleteAppliance,
  deleteComponent,
  SbCatalog,
} from '../../../services/serviceBooking';

export type Kind = 'jobTypes' | 'appliances' | 'components' | 'attributeOptions';

export const NOUN: Record<Kind, string> = {
  jobTypes: 'enquiry type',
  appliances: 'equipment',
  components: 'component',
  attributeOptions: 'intake option',
};

const slug = (s: string) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const upsertFor: Record<Kind, (b: any) => Promise<any>> = {
  jobTypes: upsertJobType,
  appliances: upsertAppliance,
  components: upsertComponent,
  attributeOptions: upsertAttributeOption,
};

type Snackbar = (msg: string, sev?: 'success' | 'error' | 'warning' | 'info') => void;

/**
 * Shared catalog-editing behaviour (create / edit / toggle / delete) plus the
 * EditorDialog element. Used by both the Enquiry-Types list and the per-enquiry
 * detail screen so the management logic lives in exactly one place.
 *
 * `catalog` powers the dropdown options; `reload` is called after every change.
 */
export function useCatalogEditor(
  catalog: SbCatalog,
  reload: () => Promise<void> | void,
  showSnackbar: Snackbar,
) {
  const [editor, setEditor] = useState<{
    open: boolean;
    kind: Kind;
    data: Record<string, any>;
    isEdit: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingKey, setTogglingKey] = useState('');

  const jobTypeOptions = useMemo(
    () => catalog.jobTypes.map((j) => ({ value: j.key, label: j.label })),
    [catalog.jobTypes],
  );
  const applianceOptions = useMemo(
    () => catalog.appliances.map((a) => ({ value: a.key, label: a.label })),
    [catalog.appliances],
  );

  // No `key`/`value`/`listKey` fields — they're derived from the label.
  const fieldsFor = (kind: Kind): FieldDef[] => {
    switch (kind) {
      case 'jobTypes':
        return [
          { name: 'label', label: 'Name', type: 'text', required: true },
          {
            name: 'group',
            label: 'Enquiry kind',
            type: 'select',
            options: [
              { value: 'primary', label: 'Primary — Booking-based (appliance → component flow)' },
              { value: 'secondary', label: 'Secondary — Request-based (separate flow)' },
            ],
            required: true,
          },
          { name: 'sortOrder', label: 'Sort order', type: 'number' },
          { name: 'icon', label: 'Icon', type: 'image' },
          { name: 'images', label: 'Banner images', type: 'gallery' },
          { name: 'isActive', label: 'Enabled', type: 'switch' },
        ];
      case 'appliances':
        return [
          { name: 'label', label: 'Name (e.g. AC)', type: 'text', required: true },
          { name: 'description', label: 'Description (e.g. Air-to-Air Heat Pump)', type: 'text' },
          { name: 'supportedJobTypeKeys', label: 'Offered under enquiry types (empty = all)', type: 'multiselect', options: jobTypeOptions },
          { name: 'sortOrder', label: 'Sort order', type: 'number' },
          { name: 'icon', label: 'Icon', type: 'image' },
          { name: 'images', label: 'Images', type: 'gallery' },
          { name: 'isActive', label: 'Enabled', type: 'switch' },
        ];
      case 'components':
        return [
          { name: 'label', label: 'Name (e.g. Wall Mount)', type: 'text', required: true },
          { name: 'applianceKeys', label: 'Used by equipment (shared pools)', type: 'multiselect', options: applianceOptions },
          { name: 'basePrice', label: 'Base price (£)', type: 'number' },
          { name: 'icon', label: 'Icon', type: 'image' },
          { name: 'images', label: 'Images', type: 'gallery' },
          { name: 'triggersIntakeForm', label: 'Is the “Other” option (opens intake form)', type: 'switch' },
          { name: 'isActive', label: 'Enabled', type: 'switch' },
        ];
      case 'attributeOptions':
        return [
          { name: 'label', label: 'Label (e.g. Not Cooling)', type: 'text', required: true },
          { name: 'sortOrder', label: 'Sort order', type: 'number' },
          { name: 'isActive', label: 'Enabled', type: 'switch' },
        ];
      default:
        return [];
    }
  };

  const defaultsFor = (kind: Kind): Record<string, any> => {
    switch (kind) {
      case 'jobTypes':
        return { label: '', group: 'primary', sortOrder: 0, icon: '', images: [], isActive: true };
      case 'appliances':
        return { label: '', description: '', supportedJobTypeKeys: [], sortOrder: 0, icon: '', images: [], isActive: true };
      case 'components':
        return { label: '', applianceKeys: [], basePrice: 0, icon: '', images: [], triggersIntakeForm: false, isActive: true };
      case 'attributeOptions':
        return { listKey: 'ac_type', label: '', sortOrder: 0, isActive: true };
      default:
        return {};
    }
  };

  const openCreate = (kind: Kind, presets: Record<string, any> = {}) =>
    setEditor({ open: true, kind, isEdit: false, data: { ...defaultsFor(kind), ...presets } });
  const openEdit = (kind: Kind, row: Record<string, any>) =>
    setEditor({ open: true, kind, isEdit: true, data: { ...defaultsFor(kind), ...row } });

  const handleSave = async (values: Record<string, any>) => {
    if (!editor) return;
    const kind = editor.kind;
    const data: Record<string, any> = { ...values };
    if (kind === 'attributeOptions') {
      if (!data.value) data.value = slug(data.label);
    } else if (!data.key) {
      data.key = slug(data.label);
    }
    try {
      setSaving(true);
      await upsertFor[kind](data);
      showSnackbar('Saved', 'success');
      setEditor(null);
      await reload();
    } catch (err: any) {
      showSnackbar(err?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const quickToggle = async (kind: Kind, row: Record<string, any>, next: boolean) => {
    const rowKey = row._id || row.key || `${row.listKey}:${row.value}`;
    try {
      setTogglingKey(rowKey);
      const body =
        kind === 'attributeOptions'
          ? { listKey: row.listKey, value: row.value, isActive: next }
          : { key: row.key, isActive: next };
      await upsertFor[kind](body);
      await reload();
    } catch (err: any) {
      showSnackbar(err?.message || 'Update failed', 'error');
    } finally {
      setTogglingKey('');
    }
  };

  const handleDelete = async (kind: Kind, row: Record<string, any>) => {
    const name = row.label || row.value;
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      if (kind === 'jobTypes') await deleteJobType(row.key);
      else if (kind === 'appliances') await deleteAppliance(row.key);
      else if (kind === 'components') await deleteComponent(row.key);
      else {
        showSnackbar('Intake options can’t be deleted — disable instead', 'info');
        return;
      }
      showSnackbar('Deleted', 'success');
      await reload();
    } catch (err: any) {
      showSnackbar(err?.message || 'Delete failed', 'error');
    }
  };

  const editorElement = editor ? (
    <EditorDialog
      open={editor.open}
      title={`${editor.isEdit ? 'Edit' : 'Add'} ${NOUN[editor.kind]}`}
      fields={fieldsFor(editor.kind)}
      initial={editor.data}
      isEdit={editor.isEdit}
      saving={saving}
      onClose={() => setEditor(null)}
      onSave={handleSave}
      onError={(msg) => showSnackbar(msg, 'error')}
    />
  ) : null;

  return { openCreate, openEdit, quickToggle, handleDelete, togglingKey, editorElement };
}
